import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import useFetchProduct from "../../adminDashboard/products/useFetchProduct";
import useGuestCart from "./useGuestCart";
import { API } from "../../../config";

function GuestCart() {
  const {
    guestCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    isUpdatingQuantity,
  } = useGuestCart();
  const [productDetails, setProductDetails] = useState({});
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { getOneProductFn } = useFetchProduct();

  const fetchProductDetails = async (productId) => {
    try {
      const product = await getOneProductFn(productId);
      setProductDetails((prev) => ({
        ...prev,
        [productId]: product,
      }));
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product details");
    }
  };

  useEffect(() => {
    const fetchAllProductDetails = async () => {
      setIsLoadingProducts(true);
      if (guestCart?.length) {
        const promises = guestCart.map((item) =>
          fetchProductDetails(item.productId)
        );
        await Promise.all(promises);
      }
      setIsLoadingProducts(false);
    };

    fetchAllProductDetails();
  }, [guestCart]);

  const handleQuantityUpdate = (productId, newQuantity) => {
    try {
      updateQuantity({ productId, quantity: newQuantity });
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  const handleRemoveItem = (productId) => {
    try {
      removeFromCart(productId);
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item");
    }
  };

  const handleClearCart = () => {
    try {
      clearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
      toast.error("Failed to clear cart");
    }
  };

  const calculateTotal = () => {
    return guestCart.reduce((total, item) => {
      const product = productDetails[item.productId];
      const price = product?.price || 0;
      const quantity = item.quantity || 0;
      return total + price * quantity;
    }, 0);
  };

  // if (isLoadingProducts) {
  //   return (
  //     <div className="flex justify-center items-center min-h-[400px]">
  //       <CustomLoading size="2rem" />
  //     </div>
  //   );
  // }

  if (!guestCart.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-2xl font-semibold text-gray-600">
          Your cart is empty
        </p>
        <Link
          to="/"
          className="bg-[#DA0E1A] text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  const subtotal = calculateTotal();
  const shipping = subtotal > 0 ? 50 : 0; // Example shipping cost
  const total = subtotal + shipping;

  return (
    <div className="container py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-6">
            Your Cart ({guestCart.length} items)
          </h2>
          <div className="space-y-4">
            {guestCart.map((item, i) => {
              const product = productDetails[item.productId];
              return (
                <div
                  key={item.productId || i}
                  className="flex items-center gap-4 p-2 justify-between md:p-4 bg-white rounded-lg shadow md:flex-row flex-col"
                >
                  <div className="flex items-center gap-2 bg-white rounded-lg md:py-4">
                    <img
                      src={`${API}/products/photo/${item.productId}`}
                      alt={product?.name || "Product"}
                      className="w-24 h-24 object-contain"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold">{product?.name}</h3>
                      <p className="text-green-600 font-bold">
                        {product?.price} EGP
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() =>
                            handleQuantityUpdate(
                              item.productId,
                              Math.max(1, item.quantity - 1)
                            )
                          }
                          className="px-2 py-1 border rounded"
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            handleQuantityUpdate(
                              item.productId,
                              item.quantity + 1
                            )
                          }
                          className="px-2 py-1 border rounded"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveItem(item.productId)}
                    className="text-red-500 hover:text-red-700 md:px-4"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:w-80">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold mb-4">Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{subtotal} EGP</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{shipping} EGP</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>{total} EGP</span>
                </div>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              <Link
                to="/signin"
                className="block w-full text-center bg-[#DA0E1A] text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
              >
                Sign in to Checkout
              </Link>
              <button
                onClick={handleClearCart}
                className="block w-full text-center bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuestCart;
